// eslint-disable-next-line no-unused-vars
import React, { useEffect, useRef } from 'react';
// import PropTypes from 'prop-types';
import { useAuth } from '../useAuth'; // Adjust path to your useAuth hook
import { useTranslation } from 'react-i18next'; // Import i18next hook

/**
 * Injects and manages the Tawk.to chat widget.
 * Personalizes the widget with user name and email if available.
 * Sends the current application language as a custom attribute.
 *
 * @component
 * @example
 * return <TawkToChat />
 */
const TawkToChat = () => {
    const { currentUser } = useAuth(); // Get user info from context
    const { i18n } = useTranslation(); // Get i18n instance for language
    const tawkLoaded = useRef(false); // Ref to track if API interaction has happened

    // Effect for loading the Tawk.to script (runs only once on mount)
    useEffect(() => {
        // Prevent duplicate script injection if navigating within SPA
        if (window.Tawk_API || document.getElementById('tawkto-script')) {
            console.log("Tawk.to script likely already loaded.");
            // Ensure API object exists for subsequent effects
            window.Tawk_API = window.Tawk_API || {};
            // Manually trigger API setup if script already loaded but API might not be set up yet
            // This covers cases where component re-mounts but script persists
            if (window.Tawk_API?.getStatus?.()) { // Check if API seems ready
                tawkLoaded.current = false; // Reset ref to allow setup in the next effect
            }
            return;
        }

        console.log("Loading Tawk.to script...");
        // Initialize the Tawk_API object (important for async loading)
        window.Tawk_API = window.Tawk_API || {};
        window.Tawk_LoadStart = new Date(); // Tawk.to recommendation

        const tawkScript = document.createElement("script");
        tawkScript.id = 'tawkto-script'; // Add ID for checking existence
        tawkScript.async = true;
        // Replace with your actual Tawk.to widget ID/property ID
        tawkScript.src = "https://embed.tawk.to/667ef7e89d7f358570d4997f/1i1fvgpcl";
        tawkScript.charset = "UTF-8";
        tawkScript.setAttribute("crossorigin", "*");

        const firstScriptTag = document.getElementsByTagName("script")[0];
        if (firstScriptTag && firstScriptTag.parentNode) {
            firstScriptTag.parentNode.insertBefore(tawkScript, firstScriptTag);
        } else {
            // Fallback if no script tag exists (less likely but safe)
            document.head.appendChild(tawkScript);
        }


        // Basic cleanup: remove the script tag if the component unmounts.
        // Note: Tawk.to might still persist some things in window object.
        return () => {
            const script = document.getElementById('tawkto-script');
            if (script && script.parentNode) {
                console.log("Cleaning up Tawk.to script tag...");
                script.parentNode.removeChild(script);
                // Reset ref if component unmounts fully
                tawkLoaded.current = false;
                // Optionally delete window.Tawk_API but can cause issues if other components use it
                // delete window.Tawk_API;
            }
        };
    }, []); // Empty dependency array: runs only once on mount

    // Effect for setting visitor data and language attribute *after* API is ready
    useEffect(() => {
        // Function to set attributes and visitor info
        const setupVisitorAndAttributes = () => {
            // Check if the API object and necessary methods exist
            if (window.Tawk_API && typeof window.Tawk_API.setAttributes === 'function' && !tawkLoaded.current) {
                // console.log("Tawk.to API ready. Setting visitor info and attributes.");

                // 1. Set Visitor Info (if user is logged in)
                if (currentUser) {
                    window.Tawk_API.visitor = {
                        name: currentUser?.displayName || undefined, // Use undefined if null/empty
                        email: currentUser?.email || undefined,
                        // hash: hmac_hash, // Optional: If using secure mode
                    };
                    // console.log("Tawk.to visitor set:", window.Tawk_API.visitor);
                } else {
                    // Clear visitor data if user logs out while component is mounted
                    window.Tawk_API.visitor = {};
                }

                // 2. Set Language Attribute
                const currentLanguage = i18n.languages[0]; // e.g., 'en', 'fr', 'en-US'
                window.Tawk_API.setAttributes({
                    'language': currentLanguage, // Send the current language code
                    'userId': currentUser?.uid || 'anonymous', // Send user ID if available
                    'displayName': currentUser?.displayName || 'Valued User', // Send user name if available
                    'email': currentUser?.email || '', // Send user email if available
                    'name': currentUser?.displayName || 'Valued User', // Send user name if available

                    // Add any other relevant attributes
                    // 'plan': userPlan || 'free',
                }, function (error) {
                    if (error) {
                        console.error('Tawk.to Error setting attributes:', error);
                    } else {
                        // console.log('Tawk.to attributes set:', { language: currentLanguage });
                    }
                });

                tawkLoaded.current = true; // Mark as loaded/configured for this session
            } else if (tawkLoaded.current) {
                // console.log("Tawk.to API already configured in this session.");
            }
            else {
                // console.log("Tawk.to API not ready yet...");
            }
        };

        // Tawk.to recommends using onLoad, but it can be tricky with React hooks.
        // A common alternative is polling or checking periodically after mount.
        if (!tawkLoaded.current) {
            // Check immediately and then set an interval just in case it loads later
            setupVisitorAndAttributes(); // Try immediately

            const intervalId = setInterval(() => {
                if (window.Tawk_API && typeof window.Tawk_API.setAttributes === 'function') {
                    setupVisitorAndAttributes();
                    clearInterval(intervalId); // Stop checking once loaded
                } else if (Date.now() - (window.Tawk_LoadStart || Date.now()) > 15000) {
                    // Give up after 15 seconds to avoid infinite loops
                    // console.warn("Tawk.to API did not load within 15 seconds.");
                    clearInterval(intervalId);
                }
            }, 1000); // Check every second

            return () => clearInterval(intervalId); // Cleanup interval on unmount or dependency change
        }

    }, [currentUser, i18n.languages]); // Rerun if user or language changes

    // The Tawk.to script injects the widget itself, so this component renders null.
    return null;
};

// No external props needed currently
TawkToChat.propTypes = {};

export default TawkToChat;